import { Trans, useLingui } from '@lingui/react/macro';
import { MediaManagerTemplateGroup } from 'modules/manager/domain/interfaces/media-manager-template-group';
import { TEMPLATE_NAME_CONFLICT_MESSAGE, isTemplateGroupNameConflict } from 'modules/manager/domain/template-name-conflict';
import { useUpdateMediaManagerTemplateGroupMutation } from 'modules/manager/store/media-manager-api';
import MediaManagerTemplateGroupForm, {
  TemplateGroupFormValues,
} from 'modules/manager/ui/components/forms/media-manager-template-group-form';
import { useCallback, useMemo, useState } from 'react';
import { UDText } from 'ud-ui/components';
import Modal from 'ud-ui/components/modal';

interface Args {
  groups: MediaManagerTemplateGroup[];
}

/**
 * The group list and the championship «Медиа» tab (it shows the set's name) refetch by the
 * mutation's tags (IMGM-1515).
 */
function useRenameTemplateGroupModal({ groups }: Args) {
  const { t } = useLingui();
  const [updateGroup, { isLoading }] = useUpdateMediaManagerTemplateGroupMutation();
  const [group, setGroup] = useState<MediaManagerTemplateGroup | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = useCallback((next: MediaManagerTemplateGroup) => {
    setError(null);
    setGroup(next);
    setIsOpen(true);
  }, []);
  const close = useCallback(() => setIsOpen(false), []);

  const existingNames = useMemo(() => groups.filter((item) => item.id !== group?.id).map((item) => item.name.trim()), [groups, group]);

  const onSubmit = useCallback(
    async (values: TemplateGroupFormValues) => {
      if (!group) return;
      setError(null);
      try {
        await updateGroup({ id: group.id, name: values.name.trim() }).unwrap();
        close();
      } catch (err) {
        console.error('Failed to rename media manager template group:', err);
        setError(
          isTemplateGroupNameConflict(err)
            ? t(TEMPLATE_NAME_CONFLICT_MESSAGE)
            : t`Не удалось переименовать группу, попробуйте ещё раз`,
        );
      }
    },
    [group, updateGroup, close, t],
  );

  return {
    open,
    close,
    modal: (
      <Modal show={isOpen} onClose={close} size="md">
        <UDText type="title-3" className="mb-5">
          <Trans>Переименовать группу</Trans>
        </UDText>
        <MediaManagerTemplateGroupForm
          key={group?.id ?? 'none'}
          initialValues={{ name: group?.name ?? '' }}
          existingNames={existingNames}
          isLoading={isLoading}
          errorMessage={error}
          submitLabel={<Trans>Сохранить</Trans>}
          onSubmit={onSubmit}
        />
      </Modal>
    ),
  };
}

export default useRenameTemplateGroupModal;
